'use client';

import { useRef } from 'react';
import Link from 'next/link';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import Button from '@/components/Button';
import { noop } from '@/lib/constants';

type AttributeCardProps = {
  title: string;
  bodyText: string;
  // TODO: find the correct type for the lottie.json files.
  lottie: unknown;
  href: string;
  hasHeader: boolean;
};

export function AttributeCard({ title, bodyText, lottie, href, hasHeader }: AttributeCardProps) {
  const containerRef = useRef(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  const handleOnMouseEnter = () => {
    lottieRef.current?.setDirection(1);
    lottieRef.current?.play();
  };

  const handleOnMouseLeave = () => {
    lottieRef.current?.setDirection(-1);
    lottieRef.current?.play();
  };

  return (
    <Link
      id="attribute-card"
      ref={containerRef}
      href={href}
      className={
        'group col-span-1 flex w-full flex-col gap-6 rounded-2xl bg-white shadow-md transition-shadow hover:shadow-lg'
      }
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
    >
      {hasHeader && (
        <div className="h-40 max-h-40 overflow-hidden rounded-t-2xl">
          <Lottie
            animationData={lottie}
            autoplay={false}
            lottieRef={lottieRef}
            loop={false}
            className="relative top-[50%] -translate-y-[50%]"
          />
        </div>
      )}
      <div className="flex flex-col gap-6 px-4 pb-6">
        <div className="flex flex-col gap-2">
          <h3 className="text-eyebrow-strong text-gray-1">{title}</h3>
          <p className="text-body line-clamp-3 min-h-11 overflow-y-hidden overflow-ellipsis text-gray-3 md:min-h-12 lg:min-h-[3.125rem] xl:min-h-14">
            {bodyText}
          </p>
        </div>
        <div className="flex items-center justify-center">
          <span className="text-body mr-auto text-gray-1 transition-colors group-hover:text-gradle-blue">
            Explore more
          </span>
          <Button
            outline
            variant="ghost-light"
            size="xs"
            className="self-end from-primary to-secondary outline outline-1 outline-gray-5 transition-[background-image_translate] duration-300 group-hover:-translate-y-0.5 group-hover:bg-gradient-to-br group-hover:outline-none"
            roundedFull
            onClick={noop}
          >
            <ArrowRightIcon className="size-6 text-black group-hover:text-white" />
          </Button>
        </div>
      </div>
    </Link>
  );
}
